/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { Toolbar } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { dashboardActions } from 'modules/dashboard/slice';
import { summaryActions } from 'modules/dashboard/components/tab-container/summary-tab/slice';
import { selectOrganization } from 'modules/common/auth/selectors';
import {
  selectBreakdown,
  selectDateComparison,
  selectSummaryAlignmentToggle,
  selectSymmetricComparisonToggle,
  selectSummaryOtb,
  selectSummaryCustomDate,
  selectIsDetailed,
  selectSummaryEndDate,
  selectSummaryStartDate,
  selectSummaryVisualToggle,
  selectSymmetricComparisonIsDisplay,
  selectEnableSecondaryHotelGroups,
  selectHotelFocusOn,
  selectNormalizedView,
  selectSummaryHotelComparison,
  selectOriginalHotelFocusOn,
  selectIsSummaryLeapComparison,
} from 'modules/dashboard/components/tab-container/summary-tab/selectors';
import {
  selectPaceHorizon,
  selectPaceVariable,
  selectTargetDate,
  selectPmsSync,
  selectSummaryForecastFilter,
  selectHotelName,
  selectHotelGroupDropdownList,
  selectHotelDropdownList,
  selectMergedHotelList,
  selectsHotelForecast,
  selectPredefinedHotelGroups,
  selectIsDemoModeActivated,
  selectIsSecondaryHotelSetOverTimeDisabled,
  selectIsSecondaryHotelSetOverTime,
  selectInvalidSecondaryHotelList,
} from 'modules/dashboard/selectors';
import { CustomStack } from 'modules/common/components';
import { labelOrder } from 'modules/dashboard/constants';
import ActionButton from 'modules/common/components/action-button';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import DataUpdatedLabel from 'modules/dashboard/components/tab-container/data-updated-label';
import PmsToggle from 'modules/dashboard/components/tab-container/inner-filter/components/pms-toggle';
import BookingWindow from 'modules/dashboard/components/tab-container/inner-filter/components/booking-window';
import BreakdownWindow from 'modules/dashboard/components/tab-container/inner-filter/components/breakdown-window';
import VsComparisonWindow from 'modules/dashboard/components/tab-container/inner-filter/components/vs-comparison-window';
import BookingPaceWindow from 'modules/dashboard/components/tab-container/inner-filter/components/booking-pace-window';
import useSummaryCommandPipeline from 'modules/dashboard/components/tab-container/summary-tab/hooks/use-summary-command-pipeline';
import { CompareHotelsWindow } from '../../../inner-filter';
import useSummaryFilterOptions from '../../hooks/use-summary-filter-options';
import useSummaryFilterHandlers from '../../hooks/use-summary-filter-handlers';

/**
 * Summary filters for booking dates, breakdown, hotel comparison and booking pace
 * @param {String} reportType - Report type of the active tab
 * @param {JSX.Element} visual - Embedded symmetric comparison visual
 * @param {String} visualName - Display name of the symmetric comparison visual
 * @param {Number} tabValue - Summary tab value
 * @param {Boolean} toggleDisabled - PMS sync toggle disabled state
 * @param {String} latestDate - Latest booking date of the selected hotel
 * @param {String} hotelTimezone - Timezone of the selected hotel
 * @param {Boolean} isLoaded - Whether the visuals are ready for filter commands
 * @returns {JSX.Element} Summary filter controls
 */
const SummaryFilter = ({
  reportType,
  visual,
  visualName,
  tabValue,
  toggleDisabled,
  latestDate,
  hotelTimezone,
  isLoaded,
}) => {
  const dispatch = useDispatch();
  const organization = useSelector(selectOrganization);
  // Selected filter values are obtained from redux store. Refer each selector function for details.
  const chartBreakdown = useSelector(selectBreakdown);
  const dateComparison = useSelector(selectDateComparison);
  const targetDate = useSelector(selectTargetDate);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const paceHorizon = useSelector(selectPaceHorizon);
  const paceVariable = useSelector(selectPaceVariable);
  const alignmentToggle = useSelector(selectSummaryAlignmentToggle);
  const symmetricComparisonToggle = useSelector(selectSymmetricComparisonToggle);
  const customBooking = useSelector(selectSummaryOtb);
  const isDetailed = useSelector(selectIsDetailed);
  const customDate = useSelector(selectSummaryCustomDate);
  const startDate = useSelector(selectSummaryStartDate);
  const endDate = useSelector(selectSummaryEndDate);
  const visualToggle = useSelector(selectSummaryVisualToggle);
  const symmetricComparisonIsDisplay = useSelector(selectSymmetricComparisonIsDisplay);
  const summaryForecastFilter = useSelector(selectSummaryForecastFilter);
  const hotelList = useSelector(selectHotelDropdownList);
  const hotelFocusOn = useSelector(selectHotelFocusOn);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const hotelGroupList = useSelector(selectHotelGroupDropdownList);
  const normalizedViewToggle = useSelector(selectNormalizedView);
  const isSummaryHotelComparison = useSelector(selectSummaryHotelComparison);
  const mergedHotelList = useSelector(selectMergedHotelList);
  const isHotelForecast = useSelector(selectsHotelForecast);
  const predefinedHotelGroups = useSelector(selectPredefinedHotelGroups);
  const originalHotelFocusOn = useSelector(selectOriginalHotelFocusOn);
  const isDemoModeActivated = useSelector(selectIsDemoModeActivated);
  const isHotelSetOverTimeDisabled = useSelector(selectIsSecondaryHotelSetOverTimeDisabled);
  const hotelSetOverTime = useSelector(selectIsSecondaryHotelSetOverTime);
  const selectedHotelName = useSelector(selectHotelName);
  const invalidPrimaryHotels = useSelector(selectInvalidSecondaryHotelList);
  const hasLeap = useSelector(selectIsSummaryLeapComparison);
  //
  const { dateComparisonList, chartBreakdownList, paceList, paceVariableList } =
    useSummaryFilterOptions(isLoaded);
  const {
    handleOTBChange,
    clearSearch,
    handleToggleChange,
    handleComparisonChange,
    handlePaceHorizonChange,
    handleForecastFilterChange,
    handlePaceVariableChange,
    handleSymmetricComparisonChange,
    handleCustomDates,
    handleBreakdownChange,
    handleIsDetailedChange,
  } = useSummaryFilterHandlers(tabValue);
  // Summary filter command flow implementation
  useSummaryCommandPipeline(tabValue, {
    toggleDisabled,
    isLoaded,
    customBooking,
    paceList,
    chartBreakdownList,
    dateComparison,
    organization,
    hotelList,
    hotelGroupList: [...hotelGroupList, ...predefinedHotelGroups],
    isHotelForecast,
    handleComparisonChange,
    handleToggleChange,
  });
  //
  return (
    <>
      <PmsToggle
        disabled={toggleDisabled}
        value={pmsSyncToggle}
        onChange={(event) => dispatch(dashboardActions.setPmsSync(event.target.checked))}
      />
      <br />
      <BookingWindow
        vOTB={customBooking}
        customBooking={customBooking}
        handleOTBChange={handleOTBChange}
        reportType={reportType}
        latestDate={latestDate}
        otbValue={false}
      />
      <br />
      <BreakdownWindow
        isDetailed={isDetailed}
        chartBreakdown={chartBreakdown}
        chartBreakdownList={chartBreakdownList?.sort(
          (first, second) => labelOrder.indexOf(first.label) - labelOrder.indexOf(second.label)
        )}
        handleIsDetailedChange={handleIsDetailedChange}
        handleBreakdownChange={handleBreakdownChange}
        isFocusOnAvailable={false}
      />
      <br />
      <CompareHotelsWindow
        selectedHotelName={selectedHotelName}
        hotelList={hotelList ?? []}
        mergedHotelList={mergedHotelList ?? []}
        focusOn={hotelFocusOn ?? []}
        focusOnLabel={hotelFocusOn?.length ? 'Compared with' : 'Select Hotel'}
        handleCompareHotelsChange={(event, value) => {
          dispatch(summaryActions.setHotelFocusOn(value));
          dispatch(dashboardActions.setIsSecondaryHotelSetOverTime(false));
        }}
        normalizedViewToggle={normalizedViewToggle}
        handleTrendHotelNormalize={(event) =>
          dispatch(summaryActions.setNormalizedView(event.target.checked))
        }
        enableSecondaryHotelGroups={enableSecondaryHotelGroups}
        hotelGroupList={hotelGroupList}
        originalHotelFocusOn={originalHotelFocusOn}
        isDemoModeActivated={isDemoModeActivated}
        setOriginalHotelFocusOn={(focus) => {
          dispatch(summaryActions.setOriginalHotelFocusOn(focus));
        }}
        resetFocusOn={(focus) => {
          dispatch(summaryActions.resetHotelFocusOn(focus));
        }}
        hotelSetOverTime={hotelSetOverTime}
        handleHotelSetChange={(event) => {
          dispatch(dashboardActions.setIsSecondaryHotelSetOverTime(event.target.checked));
          if (hotelFocusOn?.id === selectedHotelName?.id) {
            dispatch(dashboardActions.setIsPrimaryHotelSetOverTime(event.target.checked));
          }
        }}
        isHotelSetOverTimeDisabled={isHotelSetOverTimeDisabled}
        invalidPrimaryHotels={invalidPrimaryHotels}
      />
      <br />
      <VsComparisonWindow
        customBooking={customBooking}
        alignmentToggle={alignmentToggle ?? false}
        handleToggleChange={handleToggleChange}
        symmetricComparisonToggle={symmetricComparisonToggle}
        visual={visual}
        visualName={visualName}
        dateComparison={dateComparison}
        dateComparisonList={dateComparisonList}
        handleComparisonChange={handleComparisonChange}
        handleSymmetricComparisonChange={handleSymmetricComparisonChange}
        isCustomOptionAvailable
        customDate={customDate}
        reportType={reportType}
        startDate={startDate}
        endDate={endDate}
        targetDate={targetDate}
        handleCustomDates={handleCustomDates}
        isDisplay={symmetricComparisonIsDisplay}
        isDisplayOnClickAction={() => {
          dispatch(summaryActions.setSymmetricComparisonIsDisplay(!symmetricComparisonIsDisplay));
        }}
        isTimeComparisonDisabled={isSummaryHotelComparison}
        hasLeap={hasLeap}
      />
      <br />
      <BookingPaceWindow
        paceHorizon={paceHorizon}
        paceList={paceList}
        paceVariable={paceVariable}
        handlePaceHorizonChange={handlePaceHorizonChange}
        handlePaceVariableChange={handlePaceVariableChange}
        isPlot={false}
        paceVariableList={paceVariableList}
        visualToggle={visualToggle}
        handleVisualToggleChange={(event) =>
          dispatch(summaryActions.setSummaryVisualToggle(event.target.value))
        }
        handleForecastFilterChange={handleForecastFilterChange}
        summaryForecastFilter={summaryForecastFilter != null ? summaryForecastFilter : false}
        enableForecastFilter={organization?.enableForecastFilter && isHotelForecast}
        showFilter
      />
      <br />
      <CustomStack cssClassName={styles.innerFilter}>
        <ActionButton onClick={clearSearch} label="RESET" />
      </CustomStack>
      <Toolbar />
      <DataUpdatedLabel
        latestDate={latestDate}
        hotelTimezone={hotelTimezone}
        toggleDisabled={toggleDisabled}
      />
    </>
  );
};
//
export default SummaryFilter;
